import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Phone, Instagram, Facebook, Clock } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_growth-eleven11/artifacts/bn2zsn8f_IMG_3514%20%281%29.PNG";

// Calendly popup function
const openCalendly = (e) => {
  e.preventDefault();
  if (window.Calendly) {
    window.Calendly.initPopupWidget({
      url: 'https://calendly.com/studioeleven380/30min'
    });
  }
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'Custom Websites', path: '/services#custom-websites' },
    { name: 'Landing Pages', path: '/services#landing-pages' },
    { name: 'E-Commerce', path: '/services#ecommerce' },
    { name: 'UI/UX Design', path: '/services#ui-ux' },
    { name: 'Website Optimization', path: '/services#optimization' },
    { name: 'Deployment & Maintenance', path: '/services#deployment' },
  ];

  const company = [
    { name: 'About Us', path: '/about' },
    { name: 'Case Studies', path: '/case-studies' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <footer data-testid="main-footer" className="bg-[#050505] border-t border-white/10">
      {/* CTA Section */}
      <div className="container-custom py-20">
        <div className="max-w-3xl">
          <span className="overline">Ready to Start?</span>
          <h2 className="heading-section text-[#F5F5F7] mt-4 mb-6">
            Let's build a website that works as hard as you do.
          </h2>
          <button
            onClick={openCalendly}
            data-testid="footer-cta-button"
            className="btn-primary inline-flex"
          >
            Start Your Project
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="divider-gold" />

      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" data-testid="footer-logo">
              <img 
                src={LOGO_URL} 
                alt="Eleven11 Studios" 
                className="h-10 w-auto invert mb-6"
              />
            </Link>
            <p className="text-[#71717A] text-sm leading-relaxed mb-6">
              A premium web design & development studio building high-performance 
              websites for modern businesses.
            </p>
            <div className="flex flex-col gap-3">
              <a 
                href="mailto:hello@eleven11-studios.com"
                data-testid="footer-email-primary"
                className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm"
              >
                <Mail className="w-4 h-4" />
                hello@eleven11-studios.com
              </a>
              <a 
                href="mailto:studioeleven380@gmail.com"
                data-testid="footer-email"
                className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm pl-6"
              >
                studioeleven380@gmail.com
              </a>
              <a 
                href="tel:+917081111181"
                data-testid="footer-phone"
                className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm"
              >
                <Phone className="w-4 h-4" />
                +91-7081111181
              </a>
              <span className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                <Clock className="w-4 h-4" />
                Mon–Fri, 10 AM – 6 PM IST
              </span>
              <span className="flex items-start gap-2 text-[#A1A1AA] text-sm">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>22-23, Block S, DLF Phase 3, Gurugram, Haryana, India 122002</span>
              </span>
              <span className="flex items-center gap-2 text-[#71717A] text-xs pl-6">
                Serving clients in USA & Canada
              </span>
            </div>

            {/* Social Media Links */}
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://www.instagram.com/eleven11_studios_/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-all"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <span
                data-testid="footer-facebook"
                className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center text-[#71717A] cursor-not-allowed"
                title="Coming soon"
              >
                <Facebook className="w-5 h-5" />
              </span>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-[#F5F5F7] font-semibold mb-6">Services</h4>
            <ul className="space-y-4">
              {services.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    data-testid={`footer-link-${item.name.toLowerCase().replace(/ /g, '-')}`}
                    className="text-[#71717A] hover:text-[#D4AF37] transition-colors text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="text-[#F5F5F7] font-semibold mb-6">Company</h4>
            <ul className="space-y-4">
              {company.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    data-testid={`footer-link-${item.name.toLowerCase().replace(/ /g, '-')}`}
                    className="text-[#71717A] hover:text-[#D4AF37] transition-colors text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Column */}
          <div>
            <h4 className="text-[#F5F5F7] font-semibold mb-6">Stay Updated</h4>
            <p className="text-[#71717A] text-sm mb-4">
              Get web design insights and tips delivered to your inbox.
            </p>
            <form className="flex flex-col gap-3" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Enter your email"
                data-testid="footer-newsletter-input"
                className="form-input text-sm"
              />
              <button
                type="submit"
                data-testid="footer-newsletter-submit"
                className="btn-primary text-sm justify-center"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#71717A] text-sm">
            © {currentYear} Eleven11 Studios. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/privacy-policy" className="text-[#71717A] hover:text-[#D4AF37] text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-conditions" className="text-[#71717A] hover:text-[#D4AF37] text-sm transition-colors">
              Terms & Conditions
            </Link>
            <Link to="/refund-policy" className="text-[#71717A] hover:text-[#D4AF37] text-sm transition-colors">
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
